import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Main from "./components/Main";
import AppSummary from "./components/AppSummary";
import Features from "./components/Features";
import Showcase from "./components/Showcase";
import Personalization from "./components/Personalization";
import UseCases from "./components/UseCases";
import HowItWorks from "./components/HowItWorks";
import TrustPlatform from "./components/TrustPlatform";
import FaqPreview from "./components/FaqPreview";
import Privacy from "./components/Pages";
import Footer from "./components/Footer";
import Navigation from "./components/Navigation";
import FadeIn from "./components/FadeIn";
import React, { lazy, startTransition, Suspense, useEffect, useLayoutEffect, useState } from "react";
import Contact from "./components/Contact";
import ScrollToAnchor from "./components/ScrollToAnchor";
import { HelmetProvider } from "react-helmet-async";
import ConsentBanner from "./components/ConsentBanner";
import RouteTracker from "./components/RouteTracker";
import RouteLoading from "./components/RouteLoading";

import "./index.scss";

// Everything below is route-specific content, not needed to render the
// homepage — split into its own chunk so a visit to any single page (the
// common case for organic search traffic) doesn't download the code for
// every other page too.
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const LiveAlertsPage = lazy(() => import("./pages/LiveAlertsPage"));
const SmartNotificationsPage = lazy(() => import("./pages/SmartNotificationsPage"));
const AiAssistantFeature = lazy(() => import("./pages/features/AiAssistantFeature"));
const CustomNotificationsFeature = lazy(() => import("./pages/features/CustomNotificationsFeature"));
const MatchDetailsFeature = lazy(() => import("./pages/features/MatchDetailsFeature"));
const LeagueStatsFeature = lazy(() => import("./pages/features/LeagueStatsFeature"));
const GuruVisionFeature = lazy(() => import("./pages/features/GuruVisionFeature"));
const LivePredictionsFeature = lazy(() => import("./pages/features/LivePredictionsFeature"));
const GuruSummaryFeature = lazy(() => import("./pages/features/GuruSummaryFeature"));
const PastMatchesFeature = lazy(() => import("./pages/features/PastMatchesFeature"));
const PreMatchAlertsFeature = lazy(() => import("./pages/features/PreMatchAlertsFeature"));
const TrackRecordFeature = lazy(() => import("./pages/features/TrackRecordFeature"));
const LineupsShotMapFeature = lazy(() => import("./pages/features/LineupsShotMapFeature"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

function App() {
  // Prerendered pages are captured in light mode, and hydration has to begin
  // from the same markup, or React discards the page and redraws it (a layout
  // shift on every visit). So a prerendered page hydrates light and switches
  // to the visitor's system theme right after; a page rendered from scratch
  // (dev server) starts in it.
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    if (typeof window === "undefined") return "light";
    const prerendered = (document.getElementById("root")?.childElementCount ?? 0) > 0;
    if (prerendered) return "light";
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  });

  // Theme changes go through startTransition: an urgent update reaching the
  // route before its lazy chunk has hydrated makes React drop the route's
  // prerendered HTML and render it again; a transition lets hydration finish.
  useLayoutEffect(() => {
    if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
      startTransition(() => setTheme("dark"));
    }
  }, []);

  useLayoutEffect(() => {
    // Apply theme before paint to avoid a brief flash.
    document.body.className = theme === "dark" ? "dark-mode" : "light-mode";
  }, [theme]);

  useEffect(() => {
    // If system preference changes while the app is open, keep in sync.
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => {
      startTransition(() => setTheme(e.matches ? "dark" : "light"));
    };

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", onChange);
      return () => mediaQuery.removeEventListener("change", onChange);
    }

    mediaQuery.addListener(onChange);
    return () => mediaQuery.removeListener(onChange);
  }, []);

  const toggleTheme = () => {
    startTransition(() =>
      setTheme((prevTheme) => (prevTheme === "dark" ? "light" : "dark")),
    );
  };

  return (
    <HelmetProvider>
      <Router>
        <ScrollToAnchor />
        <ConsentBanner />
        <RouteTracker />
        <div
          className={`main-container ${
            theme === "dark" ? "dark-mode" : "light-mode"
          }`}
        >
          <Navigation
            parentToChild={{ mode: theme }}
            modeChange={toggleTheme}
          />
          {/* "route-view" wraps the Suspense boundary's content: the prerender
              puts React's boundary markers inside it (scripts/prerender.mjs). */}
          <FadeIn transitionDuration={700} childClassName="route-view">
            <Suspense fallback={<RouteLoading />}>
            <Routes>
              {/* Main route includes Main, AppSummary, Features, Privacy, and Contact components */}
              <Route
                path="/"
                element={
                  <>
                    <Main />
                    <AppSummary />
                    <Features />
                    <Showcase />
                    <Personalization />
                    <UseCases />
                    <HowItWorks />
                    <TrustPlatform />
                    <Privacy />
                    <FaqPreview />
                    <Contact />
                  </>
                }
              />
              {/* Website sign-in is off for now: nothing on the site needs an
                  account (everything happens in the app). The Auth, SignUp,
                  ForgotPassword and Subscription components are kept for
                  when it comes back; old links land on the homepage. */}
              {["/auth", "/signup", "/forgot-password", "/subscription"].map(
                (path) => (
                  <Route
                    key={path}
                    path={path}
                    element={<Navigate to="/" replace />}
                  />
                ),
              )}
              {/* Separate routes for Privacy Policy and Terms & Conditions */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-conditions" element={<TermsConditions />} />

              {/* SEO Landing Pages */}
              <Route
                path="/live-football-alerts"
                element={<LiveAlertsPage />}
              />
              <Route
                path="/smart-football-notifications"
                element={<SmartNotificationsPage />}
              />
              <Route
                path="/features/ai-assistant"
                element={<AiAssistantFeature />}
              />
              <Route
                path="/features/custom-notifications"
                element={<CustomNotificationsFeature />}
              />
              <Route
                path="/features/match-details"
                element={<MatchDetailsFeature />}
              />
              <Route
                path="/features/league-stats"
                element={<LeagueStatsFeature />}
              />
              <Route
                path="/features/guru-vision"
                element={<GuruVisionFeature />}
              />
              <Route
                path="/features/live-predictions"
                element={<LivePredictionsFeature />}
              />
              <Route
                path="/features/guru-summary"
                element={<GuruSummaryFeature />}
              />
              <Route
                path="/features/past-matches"
                element={<PastMatchesFeature />}
              />
              <Route
                path="/features/pre-match-alerts"
                element={<PreMatchAlertsFeature />}
              />
              <Route
                path="/features/track-record"
                element={<TrackRecordFeature />}
              />
              <Route
                path="/features/lineups-shot-map"
                element={<LineupsShotMapFeature />}
              />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/faq" element={<FaqPage />} />

              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </FadeIn>
          <Footer />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
